import React from "react";

interface FilePreview {
  type: string;
  content?: string;
  name: string;
  extension?: string;
}

interface Props {
  replyText: string;
  setReplyText: (text: string) => void;
  selectedFiles: File[];
  previews: FilePreview[];
  isSubmitting: boolean;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleRemoveFile: (index: number) => void;
  handleSubmit: () => void;
}

const MAX = 1500; // same limit the old composer displayed (it never enforced it; this one does)

const MessageComposer: React.FC<Props> = ({
  replyText,
  setReplyText,
  selectedFiles,
  previews,
  isSubmitting,
  handleFileChange,
  handleRemoveFile,
  handleSubmit,
}) => {
  const canSubmit = !isSubmitting && (replyText.trim().length > 0 || selectedFiles.length > 0);
  const left = MAX - replyText.length;

  return (
    <section
      aria-label="Write a message"
      className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-5 sm:p-6"
    >
      <label htmlFor="booking-reply" className="mb-2 block text-base font-semibold text-neutral-900 dark:text-white">
        Send a message
      </label>
      <textarea
        id="booking-reply"
        rows={4}
        maxLength={MAX}
        value={replyText}
        onChange={(e) => setReplyText(e.target.value)}
        disabled={isSubmitting}
        placeholder="Write your message..."
        className="min-h-[112px] w-full resize-y rounded-xl border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 p-3.5 text-base text-neutral-900 dark:text-neutral-100 placeholder-neutral-400 focus:border-[#C2410C] focus:outline-none focus:ring-2 focus:ring-[#C2410C]/30 disabled:opacity-60 sm:text-sm"
      />
      <p
        className={`mt-1.5 text-right text-xs ${
          left <= 100 ? "font-medium text-red-700 dark:text-red-400" : "text-neutral-500 dark:text-neutral-400"
        }`}
        aria-live="polite"
      >
        {replyText.length} / {MAX}
      </p>

      {selectedFiles.length > 0 && (
        <ul className="mt-3 flex flex-wrap gap-2" aria-label="Attached files">
          {selectedFiles.map((file, i) => {
            const p = previews[i];
            return (
              <li
                key={`${file.name}-${i}`}
                className="flex max-w-full items-center gap-2 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 py-1.5 pl-2 pr-1"
              >
                {p?.type === "image" && p.content ? (
                  <img src={p.content} alt="" className="h-8 w-8 shrink-0 rounded object-cover" />
                ) : (
                  <span aria-hidden="true" className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-neutral-200 dark:bg-neutral-700 text-[10px] font-semibold uppercase text-neutral-700 dark:text-neutral-200">
                    {(p?.extension || file.name.split(".").pop() || p?.type || "file").slice(0, 4)}
                  </span>
                )}
                <span className="max-w-[160px] truncate text-sm text-neutral-800 dark:text-neutral-200">{file.name}</span>
                <button
                  type="button"
                  aria-label={`Remove ${file.name}`}
                  onClick={() => handleRemoveFile(i)}
                  disabled={isSubmitting}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-lg text-neutral-500 hover:bg-neutral-200 dark:hover:bg-neutral-700 disabled:opacity-50"
                >
                  ×
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <label
          htmlFor="reply-file-upload"
          className={`inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl border border-neutral-300 dark:border-neutral-600 px-5 text-sm font-medium text-neutral-900 dark:text-neutral-100 ${
            isSubmitting ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800"
          }`}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
          </svg>
          Attach files
          <input
            id="reply-file-upload"
            name="reply-file-upload"
            type="file"
            multiple
            className="sr-only"
            onChange={handleFileChange}
            disabled={isSubmitting}
            accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip"
          />
        </label>

        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="inline-flex min-h-[48px] items-center justify-center rounded-xl bg-[#C2410C] px-8 text-[15px] font-semibold text-white transition-colors hover:bg-[#9A3412] disabled:cursor-not-allowed disabled:bg-neutral-300 disabled:text-neutral-600 dark:disabled:bg-neutral-700 dark:disabled:text-neutral-400"
        >
          {isSubmitting ? "Sending..." : "Send"}
        </button>
      </div>
    </section>
  );
};

export default MessageComposer;
